import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

interface ScrollArrowProps {
  /** Délai en millisecondes avant l'apparition de la flèche après l'arrêt du scroll */
  delay?: number;
  /** Offset depuis le bas du viewport */
  bottomOffset?: number;
  /** Fonction appelée lors du clic sur la flèche */
  onScrollDown?: () => void;
  /** Classes CSS personnalisées */
  className?: string;
}

const ScrollArrow: React.FC<ScrollArrowProps> = ({
  delay = 2000,
  bottomOffset = 80,
  onScrollDown,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [arrowPosition, setArrowPosition] = useState(0);
  const [canScroll, setCanScroll] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);

  // Vérifier si on peut encore scroller vers le bas
  const checkScrollability = useCallback(() => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    const canScrollDown = scrollTop + windowHeight < documentHeight - 50;
    const atTop = scrollTop < 100;
    
    setCanScroll(canScrollDown);
    setIsAtTop(atTop);
    
    if (!canScrollDown) {
      setIsVisible(false);
    } else if (atTop) {
      // Afficher immédiatement quand on est en haut
      setIsVisible(true);
    }
  }, []);

  // Calculer la position de la flèche basée sur le viewport actuel
  const updateArrowPosition = useCallback(() => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const viewportHeight = window.innerHeight;
    const newPosition = scrollTop + viewportHeight - bottomOffset;
    setArrowPosition(newPosition);
  }, [bottomOffset]);

  // Gérer l'apparition/disparition de la flèche avec timer
  useEffect(() => {
    let scrollTimer: NodeJS.Timeout;
    let isScrolling = false;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const atTop = scrollTop < 100;
      
      // Si on est en haut, afficher immédiatement
      if (atTop) {
        setIsVisible(true);
        isScrolling = false;
      } else {
        // Masquer immédiatement la flèche pendant le scroll si on n'est pas en haut
        if (!isScrolling) {
          setIsVisible(false);
          isScrolling = true;
        }
      }

      // Mettre à jour la position et vérifier la scrollabilité
      updateArrowPosition();
      checkScrollability();

      // Réinitialiser le timer seulement si on n'est pas en haut
      if (!atTop) {
        clearTimeout(scrollTimer);
        
        // Programmer l'apparition de la flèche après l'arrêt du scroll
        scrollTimer = setTimeout(() => {
          isScrolling = false;
          // Revérifier la scrollabilité au moment de l'affichage
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const canScrollDown = currentScrollTop + windowHeight < documentHeight - 50;
          const stillAtTop = currentScrollTop < 100;
          
          if (canScrollDown && !stillAtTop) {
            setIsVisible(true);
          }
        }, delay);
      }
    };

    const handleResize = () => {
      updateArrowPosition();
      checkScrollability();
    };

    // Vérification initiale
    checkScrollability();
    updateArrowPosition();

    // Écouteurs d'événements
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(scrollTimer);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [delay, canScroll, updateArrowPosition, checkScrollability]);

  // Gérer le clic sur la flèche
  const handleClick = () => {
    setIsVisible(false);
    if (onScrollDown) {
      onScrollDown();
    } else {
      // Scroll par défaut vers le bas
      window.scrollBy({
        top: window.innerHeight * 0.8,
        behavior: 'smooth'
      });
    }
  };

  // Gérer l'accessibilité clavier
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <AnimatePresence>
      {isVisible && canScroll && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: -10 }}
          transition={{ 
            duration: 0.4, 
            ease: "easeOut",
            type: "spring",
            stiffness: 300,
            damping: 20
          }}
          style={{
            position: 'fixed',
            top: `${arrowPosition}px`,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000
          }}
          className={`pointer-events-auto ${className}`}
        >
          <motion.button
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className="group p-3 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:ring-offset-2"
            whileHover={{ 
              scale: 1.1,
              boxShadow: "0 10px 25px rgba(0,0,0,0.15), 0 0 20px rgba(249,115,22,0.3)"
            }}
            whileTap={{ scale: 0.95 }}
            aria-label="Faire défiler vers le bas"
            tabIndex={0}
          >
            <motion.div
              animate={{ y: [0, 4, 0] }}
              transition={{ 
                duration: 1.5, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
            >
              <ChevronDown className="h-5 w-5 text-gray-600 group-hover:text-orange-500 transition-colors duration-300" />
            </motion.div>
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ScrollArrow;